<script>
import Business from '@/services/Business';
import Content from "@/services/Content";
import Swal from "sweetalert2";
import modalEditUserReservations from "./modalEditUserReservations";
import modalViewEventsFields from "./modalViewEventsFields";
import modalAddEventAttendee from "./modalAddEventAttendee";

export default {
  components: {
    modalEditUserReservations,
    modalViewEventsFields,
    modalAddEventAttendee,
  },
  props: {
    data: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  data() {
    return {
      showModal: false,
      filter: "",
      sortDesc: true,
      sortBy: "reserve_by",
      memberRows: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      title: null,
      start_at: null,
      error: null,
      location: null,
      registrationLimit: null,
      image: null,
      selectedRegistration: {},
      selectedData: {},
      isBusy: false,
      userLimit: null,
      companyLimit: null,
      remainingSeats: null,
      event_id: null,
      fields: [],
      allFields: [], // Store all fields for Excel export (base + dynamic)
      attendeeFields: [
        { key: "reserved_for_name", label: "Attendee" },
        { key: "reserved_for_email", label: "Attendee Email" },
        { key: "company_name", label: "Company" },
      ],
    };
  },
  computed: {
    groupedRegistrations() {
      const groups = new Map();

      (this.memberRows || []).forEach((row) => {
        if (!row?.customer_id) {
          return;
        }

        if (!groups.has(row.customer_id)) {
          const reserveBy = [row.name, row.surname].filter(Boolean).join(" ").trim();
          groups.set(row.customer_id, {
            customer_id: row.customer_id,
            name: row.name,
            surname: row.surname,
            reserve_by: reserveBy,
            email: row.email,
            phone_number: row.phone_number,
            company_name: row.company_name,
            profile_url: row.profile_url,
            reservation: row.reservation,
            event_id: row.event_id,
            attendees: [],
            attendees_count: 0,
          });
        }

        groups.get(row.customer_id).attendees.push({ ...row });
      });

      return Array.from(groups.values()).map((group) => {
        const attendees = [...(group.attendees || [])].sort((a, b) => {
          const aIsRegistrant = this.isSelfAttendee(group, a) ? 0 : 1;
          const bIsRegistrant = this.isSelfAttendee(group, b) ? 0 : 1;
          return aIsRegistrant - bIsRegistrant;
        });

        return {
          ...group,
          attendees,
          attendees_count: attendees.length,
        };
      });
    },
    activeEventFields() {
      if (!this.data?.event_info?.event_fields) {
        return [];
      }
      // Only active fields, sorted for consistent column order
      return this.data.event_info.event_fields
        .filter((field) => field.active === 1)
        .sort((a, b) => (a.app_order || 0) - (b.app_order || 0));
    },
    // Single source of truth for dynamic (per-event) columns.
    // Both the table and the Excel export read from this, so a new
    // event field only ever needs to be configured on the backend.
    dynamicFieldColumns() {
      return this.activeEventFields.map((field) => ({
        key: field.label,
        label: field.label,
        sortable: false,
        showInTable: false,
        excelWidth: 20,
      }));
    },
    registeredCustomerIds() {
      return (this.memberRows || [])
        .map((m) => m.customer_id)
        .filter((id, index, ids) => id && ids.indexOf(id) === index);
    },
    eventDataForAdd() {
      return {
        ...(this.data || {}),
        userLimit: this.userLimit,
        registrationLimit: this.registrationLimit,
        event_info: {
          ...(this.data?.event_info || {}),
          remainingSeats: this.remainingSeats,
          userLimit: this.userLimit,
          registrationLimit: this.registrationLimit,
        },
      };
    },
    isExternalRegistration() {
      return !!(this.data?.event_info && Number(this.data.event_info.external_registration));
    },
    externalRegistrationUrl() {
      return this.data?.event_info?.external_registration_url || '';
    },
    // Drives the stat row under the event title
    eventStats() {
      if (this.isExternalRegistration) {
        return [
          { label: "Registration", value: "External", icon: "uil-link" },
        ];
      }
      return [
        { label: "Event Limit", value: this.registrationLimit, icon: "uil-ticket" },
        { label: "User Limit", value: this.userLimit, icon: "uil-user" },
        { label: "Company Limit", value: this.companyLimit, icon: "uil-building" },
        { label: "Remaining Seats", value: this.remainingSeats, icon: "uil-chair", highlight: true },
      ];
    },
  },

  methods: {
    onToggleRegistrationDetails(data) {
      data.toggleDetails();
    },
    openEditUserReservationsModal(registration) {
      this.selectedRegistration = {
        ...registration,
        event_id: this.event_id,
      };
      this.$bvModal.show('edit_user_reservations');
    },
    registrationFilter(row, filter) {
      if (!filter) {
        return true;
      }

      const term = String(filter).trim().toLowerCase();
      if (!term) {
        return true;
      }

      const registrantValues = [
        row.reserve_by,
        row.email,
        row.phone_number,
        row.company_name,
      ];

      if (registrantValues.some((value) => String(value || "").toLowerCase().includes(term))) {
        return true;
      }

      return (row.attendees || []).some((attendee) => {
        const attendeeName = this.displayAttendeeName(attendee);
        const attendeeEmail = this.displayAttendeeEmail(attendee);
        return (
          String(attendeeName).toLowerCase().includes(term) ||
          String(attendeeEmail).toLowerCase().includes(term)
        );
      });
    },
    displayAttendeeName(item) {
      if (item?.reserved_for_name) {
        return item.reserved_for_name;
      }
      return [item?.name, item?.surname].filter(Boolean).join(' ').trim() || '—';
    },
    displayAttendeeEmail(item) {
      return item?.reserved_for_email || '—';
    },
    isSelfAttendee(registration, attendee) {
      const registrantEmail = String(registration?.email || "").trim().toLowerCase();
      const attendeeEmail = String(this.displayAttendeeEmail(attendee) || "").trim().toLowerCase();
      const registrantName = String(registration?.reserve_by || "").trim().toLowerCase();
      const attendeeName = String(this.displayAttendeeName(attendee) || "").trim().toLowerCase();

      return (
        registrantEmail &&
        attendeeEmail &&
        registrantEmail === attendeeEmail &&
        registrantName === attendeeName
      );
    },
    openAddAttendeeModal() {
      this.$bvModal.show('add_event_attendee');
    },
    hasEventFields(member) {
      if (!this.activeEventFields.length) {
        return false;
      }
      return this.activeEventFields.some((field) => member[field.label]);
    },
    openViewEventsFieldsModal(member) {
      const result = [];

      this.activeEventFields.forEach((fieldDef) => {
        const value = member[fieldDef.label] || '';
        if (value) {
          result.push({ label: fieldDef.label, value });
        }
      });

      this.selectedData = result;
      this.$bvModal.show("view_events_fields");
    },

    async refreshSubscribersView() {
      await this.loadEventMembers();
    },
    updateRemainingSeatsFromMembers() {
      const limit = Number(this.registrationLimit);
      if (!limit || isNaN(limit)) {
        return;
      }

      const reservationsByCustomer = new Map();
      (this.memberRows || []).forEach((row) => {
        if (!row.customer_id || reservationsByCustomer.has(row.customer_id)) {
          return;
        }
        const reservation = parseInt(row.reservation, 10);
        reservationsByCustomer.set(row.customer_id, isNaN(reservation) ? 0 : reservation);
      });

      const reserved = Array.from(reservationsByCustomer.values()).reduce(
        (total, count) => total + count,
        0
      );
      this.remainingSeats = Math.max(0, limit - reserved);
    },
    async loadEventMembers() {
      this.isBusy = true;
      try {
        await this.eventMembersExport(this.event_id);
        this.setupTableFields();
        this.totalRows = this.groupedRegistrations.length;
        this.updateRemainingSeatsFromMembers();
      } catch (error) {
        this.error = error.response?.data?.error || "An error occurred while loading event members.";
        Swal.fire("Error", this.error, "error");
        this.memberRows = [];
        this.totalRows = 0;
        this.updateRemainingSeatsFromMembers();
      } finally {
        this.isBusy = false;
      }
    },
    setupTableFields() {
      const baseFields = [
        { key: "expand", label: "", sortable: false, thStyle: { width: "48px" } },
        { key: "customer_id", label: "Customer ID", sortable: true, showInTable: false },
        { key: "name", label: "Name", sortable: true, showInTable: false },
        { key: "surname", label: "Surname", sortable: true, showInTable: false },
        { key: "reserve_by", label: "Registrant", sortable: true },
        { key: "email", label: "Registrant Email", sortable: true },
        { key: "phone_number", label: "Phone Number", sortable: true },
        { key: "profile_url", label: "Profile", sortable: true, showInTable: false },
        { key: "company_name", label: "Company", sortable: true },
        { key: "reservation", label: "Tickets", sortable: true },
        { key: "attendees_count", label: "Attendees", sortable: true },
        { key: "reserved_for_name", label: "Attendee", sortable: true, showInTable: false },
        { key: "reserved_for_email", label: "Attendee Email", sortable: true, showInTable: false },
      ];

      // Dynamic per-event fields (e.g. dietary preferences) come from
      // dynamicFieldColumns, derived from the event's own configuration.
      this.allFields = [...baseFields, ...this.dynamicFieldColumns, "actions"];

      this.fields = this.allFields.filter((field) => {
        if (typeof field === 'string') return true; // keep 'actions'
        return field.showInTable !== false;
      });
    },
    async deleteBusinessEventRegistration(event_id, customer_id) {
      Swal.fire({
        title: "Delete User Event Registration?",
        text: "This User Event Registration will be deleted!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Delete"
      }).then(async (result) => {
        if (result.isConfirmed !== true) {
          return;
        }
        try {
          const response = await Business.deleteBusinessEventReservation(event_id, customer_id);
          if (response.data.success) {
            Swal.fire("Success!", "Business User is successfully deleted!", "success");
            await this.refreshSubscribersView();
            this.$emit('refreshScreen');
          } else {
            Swal.fire("Fail!", response.error || 'Failed', "warning");
          }
        } catch (error) {
          this.error = error.response?.data?.error || "An error occurred while removing the business event registration.";
          Swal.fire("Error", this.error, "error");
        }
      });
    },
    async eventMembersExport(event_id) {
      try {
        const response = await Content.getBusinessEventmembers(event_id);
        const payload = response?.data;
        const rows = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
        this.memberRows = rows.map((row) => ({ ...row, event_id }));
      } catch (error) {
        this.memberRows = [];
        console.error("Error loading event members:", error);
        throw error;
      }
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    // Builds the same column set used for the visible table (base fields
    // relevant to a spreadsheet + whatever dynamic event fields are active),
    // so a new event field automatically appears in the export too.
    buildExcelColumns() {
      const staticExportFields = [
        { key: 'name', header: 'Name', width: 15 },
        { key: 'surname', header: 'Surname', width: 15 },
        { key: 'phone_number', header: 'Phone Number', width: 18 },
        { key: 'profile_url', header: 'Profile Url', width: 25 },
        { key: 'company_name', header: 'Company Name', width: 20 },
        { key: 'reservation', header: 'Count', width: 10 },
        { key: 'reserved_for_name', header: 'Attendee', width: 18 },
        { key: 'reserved_for_email', header: 'Attendee Email', width: 25 },
      ];

      const dynamicExportFields = this.dynamicFieldColumns.map((field) => ({
        key: field.key,
        header: field.label,
        width: field.excelWidth || 20,
      }));

      return [...staticExportFields, ...dynamicExportFields];
    },
    async exportToExcel() {
      if (!this.memberRows || this.memberRows.length === 0) {
        Swal.fire("Warning", "No data available to export.", "warning");
        return;
      }

      try {
        const ExcelJS = require('exceljs');
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Event Members');

        const columnHeaders = this.buildExcelColumns();

        worksheet.addRow(columnHeaders.map((column) => column.header));
        worksheet.getRow(1).font = { bold: true };

        columnHeaders.forEach((column, index) => {
          worksheet.getColumn(index + 1).width = column.width || 20;
        });

        this.memberRows.forEach((item) => {
          const rowData = columnHeaders.map((column) => {
            const value = item[column.key];
            return value !== null && value !== undefined ? String(value) : '';
          });
          worksheet.addRow(rowData);
        });

        const fileName = (this.data?.title || 'Event_Members') + '.xlsx';

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error("Error exporting to Excel:", error);
        Swal.fire("Error", "Failed to export to Excel. Please try again.", "error");
      }
    },

    populateFields() {
      if (!this.data) {
        return;
      }
      this.remainingSeats = this.data.event_info?.remainingSeats || "";
      this.title = this.data.title || "";
      this.start_at = this.data.start_at || "";
      this.image = this.data.image || "";
      this.location = this.data.location || "";
      this.registrationLimit = this.data.registrationLimit || "";
      this.userLimit = this.data.userLimit || "";
      this.companyLimit = this.data.companyLimit || "";
      this.event_id = this.data.event_info?.event_id || "";

      if (this.event_id) {
        this.loadEventMembers();
      }
    },

    closeModal() {
      this.showModal = false;
    },

    refreshData() {
      this.$emit('refreshScreen');
    },
    async onAttendeeAdded() {
      await this.refreshSubscribersView();
      this.$emit('refreshScreen');
    },
    async onReservationUpdated() {
      await this.refreshSubscribersView();
      this.$emit('refreshScreen');
    },
  },
};
</script>

<template>
  <b-modal
    id="view_event_info"
    v-model="showModal"
    title="Subscribers List"
    title-class="font-18"
    size="xl"
    body-class="event-subscribers-body"
    @shown="populateFields"
  >
    <!-- Event header: name, date/location, key stats -->
    <div class="event-header mb-4">
      <div class="event-header__title">
        <h4 class="mb-1">{{ title || 'Event' }}</h4>
        <p class="event-header__meta text-muted mb-0">
          <span class="event-header__meta-item">
            <i class="uil uil-calendar-alt"></i>{{ start_at || '—' }}
          </span>
          <span class="event-header__meta-item">
            <i class="uil uil-map-marker"></i>{{ location || '—' }}
          </span>
        </p>
      </div>

      <div class="event-stats">
        <div
          v-for="stat in eventStats"
          :key="stat.label"
          class="event-stat-card"
          :class="{ 'event-stat-card--highlight': stat.highlight }"
        >
          <i class="uil event-stat-card__icon" :class="stat.icon"></i>
          <div>
            <div class="event-stat-card__value">{{ stat.value || '—' }}</div>
            <div class="event-stat-card__label">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </div>

    <p class="text-muted small mb-3">
      <i class="uil uil-info-circle me-1"></i>
      <template v-if="isExternalRegistration">
        This event uses external registration. Attendees cannot be added from the dashboard.
        <a
          v-if="externalRegistrationUrl"
          :href="externalRegistrationUrl"
          target="_blank"
          rel="noopener noreferrer"
        >Open registration URL</a>
      </template>
      <template v-else>
        Each row is a registration. Expand a row to view its attendees.
      </template>
    </p>

    <!-- Toolbar -->
    <div class="table-toolbar mb-3">
      <div class="table-toolbar__left">
        <label class="table-toolbar__per-page">
          Show
          <b-form-select v-model="perPage" size="sm" :options="pageOptions"></b-form-select>
          entries
        </label>
      </div>

      <div class="table-toolbar__right">
        <b-button
          v-if="!isExternalRegistration"
          variant="success"
          size="sm"
          class="waves-effect waves-light"
          @click="openAddAttendeeModal"
        >
          <i class="uil uil-user-plus me-1"></i>
          Add attendees
        </b-button>
        <b-button
          variant="outline-success"
          size="sm"
          class="waves-effect waves-light"
          @click="exportToExcel"
          :disabled="!memberRows || memberRows.length === 0"
        >
          <i class="uil uil-file-excel-alt me-1"></i>
          Export to Excel
        </b-button>
        <b-form-input
          v-model="filter"
          type="search"
          size="sm"
          placeholder="Search registrant or attendee..."
          class="table-toolbar__search"
        ></b-form-input>
      </div>
    </div>

    <!-- Table -->
    <div class="table-responsive mb-0">
      <b-table
        :busy="isBusy"
        :items="groupedRegistrations"
        :fields="fields"
        responsive="sm"
        :per-page="perPage"
        :current-page="currentPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :filter="filter"
        :filter-function="registrationFilter"
        show-empty
        empty-text="No registrations found"
        @filtered="onFiltered"
      >
        <template v-slot:cell(expand)="data">
          <button
            v-if="data.item.attendees && data.item.attendees.length"
            type="button"
            class="expand-toggle-btn"
            :class="{ 'is-open': data.detailsShowing }"
            title="Show attendees"
            @click="onToggleRegistrationDetails(data)"
          >
            <i class="uil uil-angle-right"></i>
          </button>
          <span v-else class="text-muted">—</span>
        </template>
        <template v-slot:cell(reserve_by)="data">
          {{ data.item.reserve_by || '—' }}
        </template>
        <template v-slot:cell(reservation)="data">
          <span class="badge bg-soft-primary text-primary">{{ data.item.reservation || 0 }}</span>
        </template>
        <template v-slot:cell(attendees_count)="data">
          <span class="badge bg-soft-secondary text-secondary">{{ data.item.attendees_count || 0 }}</span>
        </template>
        <template v-slot:cell(actions)="data">
          <ul class="list-inline mb-0 d-flex align-items-center flex-nowrap">
            <li class="list-inline-item">
              <a
                href="javascript:void(0);"
                class="px-2 text-primary"
                v-b-tooltip.hover
                title="Edit registration"
                @click="openEditUserReservationsModal(data.item)"
              >
                <i class="uil uil-pen font-size-18"></i>
              </a>
            </li>
            <li class="list-inline-item">
              <a
                href="javascript:void(0);"
                class="px-2 text-danger"
                v-b-tooltip.hover
                title="Delete registration"
                @click="deleteBusinessEventRegistration(event_id, data.item.customer_id)"
              >
                <i class="uil uil-trash-alt font-size-18"></i>
              </a>
            </li>
          </ul>
        </template>

        <template v-slot:row-details="data">
          <div class="attendees-details-table px-3 py-2">
            <h6 class="text-muted text-uppercase small mb-2">Attendees</h6>
            <b-table
              table-class="table table-centered table-sm mb-0"
              thead-tr-class="bg-transparent"
              :items="data.item.attendees"
              :fields="attendeeFields"
              responsive="sm"
              show-empty
              empty-text="No attendees found"
            >
              <template v-slot:cell(reserved_for_name)="attendeeData">
                <span>{{ displayAttendeeName(attendeeData.item) }}</span>
                <span
                  v-if="isSelfAttendee(data.item, attendeeData.item)"
                  class="badge bg-soft-primary text-white ms-1"
                >
                  Registrant
                </span>
              </template>
              <template v-slot:cell(reserved_for_email)="attendeeData">
                {{ displayAttendeeEmail(attendeeData.item) }}
              </template>
              <template v-slot:cell(actions)="attendeeData">
                <ul class="list-inline mb-0">
                  <li v-if="hasEventFields(attendeeData.item)" class="list-inline-item">
                    <a
                      href="javascript:void(0);"
                      class="px-2 text-primary"
                      v-b-tooltip.hover
                      title="View Info"
                      @click="openViewEventsFieldsModal(attendeeData.item)"
                    >
                      <i class="uil uil-clipboard-notes font-size-18"></i>
                    </a>
                  </li>
                </ul>
              </template>
            </b-table>
          </div>
        </template>

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Loading...</strong>
          </div>
        </template>
      </b-table>
    </div>

    <div class="row">
      <div class="col">
        <div class="dataTables_paginate paging_simple_numbers float-end">
          <ul class="pagination pagination-rounded mb-0">
            <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage"></b-pagination>
          </ul>
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
    </template>

    <modalEditUserReservations
      :registration="selectedRegistration"
      :user-limit="userLimit"
      :remaining-seats="remainingSeats"
      @onRefresh="onReservationUpdated"
    ></modalEditUserReservations>
    <modalViewEventsFields
      :selectedData="selectedData"
      @onRefresh="loadEventMembers()"
    />
    <modalAddEventAttendee
      :event-id="event_id"
      :event-data="eventDataForAdd"
      :registered-customer-ids="registeredCustomerIds"
      @onRefresh="onAttendeeAdded"
    />
  </b-modal>
</template>

<style scoped>
/* ---------- Event header ---------- */
.event-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eef0f2;
}

.event-header__title {
  min-width: 220px;
}

.event-header__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1rem;
  font-size: 0.875rem;
}

.event-header__meta-item i {
  margin-right: 0.35rem;
}

.event-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.event-stat-card {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 130px;
  padding: 0.6rem 0.9rem;
  background: #f8f9fa;
  border: 1px solid #eef0f2;
  border-radius: 0.5rem;
}

.event-stat-card--highlight {
  background: rgba(52, 195, 143, 0.08);
  border-color: rgba(52, 195, 143, 0.25);
}

.event-stat-card__icon {
  font-size: 1.25rem;
  color: #74788d;
}

.event-stat-card--highlight .event-stat-card__icon {
  color: #34c38f;
}

.event-stat-card__value {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.2;
}

.event-stat-card__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #74788d;
}

/* ---------- Toolbar ---------- */
.table-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.table-toolbar__per-page {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0;
  font-size: 0.875rem;
  color: #74788d;
}

.table-toolbar__per-page select {
  width: auto;
}

.table-toolbar__right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.table-toolbar__search {
  width: 220px;
}

/* ---------- Expand toggle ---------- */
.expand-toggle-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #f1f3f5;
  color: #495057;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.15s ease;
}

.expand-toggle-btn:hover {
  background: #e9ecef;
}

.expand-toggle-btn i {
  font-size: 16px;
  transition: transform 0.15s ease;
}

.expand-toggle-btn.is-open i {
  transform: rotate(90deg);
}

/* ---------- Attendee sub-table ---------- */
.attendees-details-table {
  background: #f8f9fa;
  border-radius: 0.375rem;
}

@media (max-width: 576px) {
  .table-toolbar__search {
    width: 100%;
  }

  .table-toolbar__right {
    width: 100%;
  }

  .table-toolbar__right .table-toolbar__search {
    order: 99;
  }
}
</style>